import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Button,
  Card,
  Fact,
  Facts,
  Icon,
  Page,
  PageHeader,
  Pagination,
  SearchSelect,
  Table,
  rowLinkClasses,
  type Action,
} from '@evogard/ui';
import {
  ASSISTANCE_SERVICES,
  businessDate,
  count,
  type AssistanceServiceType,
} from '@evogard/domain';
import {
  DISPATCH_LIST_FILTERS,
  type DispatchListFilter,
  type DispatchListRow,
} from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { useDebouncedTerm } from '../../lib/debounce.js';
import { useRealtimeConnected } from '../../lib/realtime-bus.js';
import { DispatchDesk } from './DispatchDesk.js';
import { ProgressCell, useNow } from './ProgressPanel.js';
import './live.js';
import { NewDispatchModal } from './NewDispatchModal.js';
import {
  ASSISTANCE_KEY,
  DISPATCHES_KEY,
  PAYMENT_LABEL,
  SERVICE_ICON,
  reais,
  situationOf,
  when,
} from './assistance.js';

/** O que a rota guarda na barra de endereço: o filtro, a mesa aberta e o modal. */
export interface DispatchesSearch {
  status?: DispatchListFilter;
  open?: number;
  novo?: 'agora' | 'agendar';
  draft?: number;
}

const PERIODS = [
  { value: '1', label: 'Hoje' },
  { value: '7', label: 'Últimos 7 dias' },
  { value: '30', label: 'Últimos 30 dias' },
  { value: '', label: 'Todo o período' },
];

// @valor
const FILTER_LABEL: Record<DispatchListFilter, string> = {
  rascunho: 'Rascunhos',
  agendado: 'Agendados',
  acionado: 'Acionados',
  cancelado: 'Cancelados',
  descartado: 'Rascunhos descartados',
};

/** O status como a linha o mostra — o rascunho descartado é um rascunho arquivado (P12). */
function badgeOf(d: DispatchListRow) {
  return d.discarded ? { label: 'Descartado', tone: 'neutro' as const } : situationOf(d);
}

/** O instante que importa em cada status, e o nome dele. */
function momentOf(d: DispatchListRow): { label: string; at: string | null } {
  if (d.discarded) return { label: 'descartado', at: d.updatedAt };
  switch (d.status) {
    case 'rascunho':
      return { label: 'salvo', at: d.updatedAt };
    case 'agendado':
      return { label: 'marcado para', at: d.scheduledFor };
    case 'acionado':
      return { label: 'acionado', at: d.executedAt };
    case 'cancelado':
      return { label: 'cancelado', at: d.canceledAt };
  }
}

/**
 * Acionamentos — a tabela única da central (29/10/2026). Rascunho, agendado,
 * acionado e cancelado são o mesmo registro em status diferentes (P15), e por
 * isso são uma tabela só, com o status como coluna e como filtro. O novo
 * acionamento abre num modal por cima dela, e o rascunho continua no mesmo
 * modal; o enviado muda o status da mesma linha, com o mesmo código.
 *
 * O rascunho é da equipe inteira (`assistencia-22`): quem pega o plantão vê o
 * que ficou e continua. Descartar arquiva, e não apaga (P12) — o descartado só
 * aparece no filtro dele. O agendado não conta até alguém executar; a Agenda
 * continua sendo a vista de calendário dele.
 *
 * O filtro, a mesa e o modal moram na URL: o link da origem da despesa no
 * Financeiro (`?open=`) e o "Agendar acionamento" da Agenda chegam por ela.
 */
export function DispatchesScreen({ search: route }: { search: DispatchesSearch }) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const navigate = useNavigate();
  const client = useQueryClient();
  const canCreate = can(context, 'assistance.dispatches', 'criar');
  const live = useRealtimeConnected();
  const now = useNow();
  const status = route.status;
  const go = (next: DispatchesSearch) =>
    void navigate({ to: '/assistance/dispatches', search: { status, ...next } });

  const [days, setDays] = useState('');
  const [serviceType, setServiceType] = useState<AssistanceServiceType | ''>('');
  const [search, setSearch] = useState('');
  const term = useDebouncedTerm(search);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const from = days
    ? businessDate(new Date(Date.now() - (Number(days) - 1) * 86_400_000))
    : undefined;
  const query = useQuery({
    queryKey: [
      ...DISPATCHES_KEY,
      tenantId,
      status,
      from,
      serviceType,
      term,
      page,
      perPage,
    ],
    enabled: tenantId !== undefined,
    /*
     * O andamento chega pelo tempo real (`live.ts`); a releitura periódica é a
     * reserva para quando o socket cai, e é mais curta sem ele.
     */
    refetchInterval: live ? 120_000 : 20_000,
    queryFn: async () =>
      ok(
        await api.assistanceDispatches.listDispatches({
          query: {
            status,
            page,
            perPage,
            from,
            serviceType: serviceType || undefined,
            search: term.trim() || undefined,
          },
        }),
        200,
        'Não foi possível carregar os acionamentos.',
      ),
  });
  const discard = useMutation({
    mutationFn: async (id: number) =>
      ok(
        await api.assistanceDispatches.discardDraft({ params: { id }, body: {} }),
        200,
        'Não foi possível descartar.',
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ASSISTANCE_KEY }),
  });
  const result = query.data;
  const error = query.error ?? discard.error;
  const openDraft = (d: DispatchListRow) => d.status === 'rascunho' && !d.discarded;

  return (
    <Page measure="cheia">
      <PageHeader
        title="Acionamentos"
        actions={
          canCreate && (
            <Button icon="mais" onClick={() => go({ novo: 'agora' })}>
              Novo acionamento
            </Button>
          )
        }
      >
        Os serviços da central em {context?.currentTenant.nome}, em todos os status: o
        rascunho que a equipe começou, o agendado que espera a data, o acionado e o
        cancelado. O prestador marca pelo link quando sai, chega, confere a placa, inicia
        e finaliza, e o andamento aparece aqui na hora.
      </PageHeader>
      {error && <Alert tone="erro">{error.message}</Alert>}
      <Card
        title={
          result ? count(result.total, 'acionamento', 'acionamentos') : 'Carregando…'
        }
        descricao={
          status === 'agendado'
            ? 'Do mais próximo para o mais distante. Não contam, não têm o link das fotos e não entram no pagamento até alguém executar.'
            : status === 'descartado'
              ? 'Descartados não contam e não são pagos. Nada é apagado.'
              : 'Do mais novo para o mais antigo, pela abertura. O rascunho enviado vira agendado ou acionado com o mesmo código.'
        }
        bleed
      >
        <Table
          paginate={false}
          search={{
            label: 'Pesquisar acionamentos',
            placeholder: 'Placa, associado, protocolo ou código',
            value: search,
            onChange: (v) => {
              setSearch(v);
              setPage(1);
            },
          }}
          toolbar={
            <div className="flex flex-wrap gap-2">
              <SearchSelect
                label="Status"
                hiddenLabel
                placeholder="Todos os status"
                options={DISPATCH_LIST_FILTERS.map((f) => ({
                  value: f,
                  label: FILTER_LABEL[f],
                }))}
                value={status ?? ''}
                onChange={(v) => {
                  setPage(1);
                  void navigate({
                    to: '/assistance/dispatches',
                    search: {
                      status: (v || undefined) as DispatchListFilter | undefined,
                    },
                  });
                }}
              />
              <SearchSelect
                label="Período"
                hiddenLabel
                options={PERIODS}
                value={days}
                onChange={(v) => {
                  setDays(v);
                  setPage(1);
                }}
              />
              <SearchSelect
                label="Tipo"
                hiddenLabel
                placeholder="Todos os tipos"
                options={ASSISTANCE_SERVICES.map((s) => ({
                  value: s.type,
                  label: s.label,
                }))}
                value={serviceType}
                onChange={(v) => {
                  setServiceType(v as AssistanceServiceType | '');
                  setPage(1);
                }}
              />
            </div>
          }
          label="Acionamentos"
          items={result?.items ?? []}
          loading={query.isLoading}
          rowKey={(d) => String(d.id)}
          rowTitle={(d) => `${d.code} · ${d.memberName ?? ''}`}
          rowIcon="documento"
          rowLink={(d) =>
            d.status !== 'rascunho' ? (
              <button
                type="button"
                className={rowLinkClasses}
                aria-label={`Abrir ${d.code}`}
                onClick={() => go({ open: d.id })}
              >
                <Icon nome="seta-direita" size={16} />
              </button>
            ) : openDraft(d) && canCreate ? (
              <button
                type="button"
                className={rowLinkClasses}
                aria-label={`Continuar ${d.code}`}
                onClick={() => go({ draft: d.id })}
              >
                <Icon nome="seta-direita" size={16} />
              </button>
            ) : null
          }
          rowActions={(d): Action[] =>
            openDraft(d) && canCreate
              ? [
                  {
                    label: 'Continuar',
                    icon: 'historico',
                    run: () => go({ draft: d.id }),
                  },
                  {
                    label: 'Descartar',
                    icon: 'bloqueado',
                    run: () => discard.mutate(d.id),
                  },
                ]
              : []
          }
          empty="Nenhum acionamento neste recorte."
          expand={(d) => <RowDetail d={d} />}
          columns={[
            {
              title: 'Acionamento',
              alignment: 'inicio' as const,
              minWidth: '11rem',
              cell: (d: DispatchListRow) => (
                <div className="flex flex-col gap-1">
                  <span className="font-mono text-xs text-texto-tenue">{d.code}</span>
                  <span className="flex items-center gap-1.5 text-sm">
                    <Icon nome={SERVICE_ICON[d.serviceType]} size={16} />
                    {
                      ASSISTANCE_SERVICES.find((s) => s.type === d.serviceType)
                        ?.shortLabel
                    }
                  </span>
                </div>
              ),
            },
            {
              title: 'Status',
              alignment: 'inicio' as const,
              minWidth: '10rem',
              cell: (d: DispatchListRow) => {
                const s = badgeOf(d);
                return (
                  <div className="flex flex-col items-start gap-1">
                    <Badge tone={s.tone}>{s.label}</Badge>
                    {d.status === 'rascunho' && !d.discarded && (
                      <span className="text-xs text-texto-tenue">
                        {d.missing.length
                          ? count(d.missing.length, 'item falta', 'itens faltam')
                          : 'pronto para enviar'}
                      </span>
                    )}
                  </div>
                );
              },
            },
            {
              title: 'Andamento',
              alignment: 'inicio' as const,
              minWidth: '9rem',
              cell: (d: DispatchListRow) =>
                d.progress ? (
                  <ProgressCell p={d.progress} now={now} />
                ) : (
                  <span className="text-texto-tenue">—</span>
                ),
            },
            {
              title: 'Associado',
              minWidth: '12rem',
              cell: (d: DispatchListRow) => (
                <div className="flex flex-col">
                  <span>{d.memberName ?? '—'}</span>
                  <span className="font-mono text-xs text-texto-tenue">
                    {d.plate ?? 'sem placa'}
                  </span>
                </div>
              ),
            },
            {
              title: 'Prestador',
              minWidth: '12rem',
              cell: (d: DispatchListRow) => <span>{d.providerName ?? '—'}</span>,
            },
            {
              title: 'Valor',
              alignment: 'fim' as const,
              cell: (d: DispatchListRow) => (
                <div className="flex flex-col items-end">
                  <span className="tabular-nums">{reais(d.amount)}</span>
                  {d.status === 'acionado' ? (
                    d.settled ? (
                      <span className="text-xs text-sucesso">pago</span>
                    ) : (
                      <span className="text-xs text-texto-tenue">a pagar</span>
                    )
                  ) : d.status === 'agendado' ? (
                    <span className="text-xs text-texto-tenue">não conta ainda</span>
                  ) : null}
                </div>
              ),
            },
            {
              title: 'Quando',
              cell: (d: DispatchListRow) => {
                const m = momentOf(d);
                return (
                  <div className="flex flex-col">
                    <span className="tabular-nums">{when(m.at)}</span>
                    <span className="text-xs text-texto-tenue">
                      {m.label}
                      {d.createdByName ? ` · ${d.createdByName}` : ''}
                    </span>
                  </div>
                );
              },
            },
          ]}
        />
        {result && (
          <Pagination
            page={page}
            perPage={perPage}
            total={result.total}
            label="acionamentos"
            onChange={setPage}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
          />
        )}
      </Card>
      {route.open !== undefined && (
        <DispatchDesk id={route.open} onClose={() => go({})} />
      )}
      {canCreate && (route.novo !== undefined || route.draft !== undefined) && (
        <NewDispatchModal
          key={route.draft ?? 'novo'}
          draftId={route.draft}
          initialWhen={route.novo}
          onDraft={(id) => go({ draft: id })}
          onClose={() => go({})}
        />
      )}
    </Page>
  );
}

/**
 * O que a linha esconde atrás do `+` (29/10/2026): o que é texto longo — o
 * itinerário, o que falta no rascunho — e o que é detalhe de quem já abriu a
 * linha. Com o itinerário na linha, um endereço do Google com bairro, cidade
 * e CEP fazia cada linha ter seis de altura.
 */
function RowDetail({ d }: { d: DispatchListRow }) {
  return (
    <div className="px-4 py-3">
      <Facts>
        <Fact label="Onde está o veículo" wide>
          {d.originLabel ?? '—'}
        </Fact>
        {d.serviceType === 'reboque' && (
          <Fact label="Para onde vai" wide>
            {d.destinationLabel ?? '—'}
          </Fact>
        )}
        {d.status === 'rascunho' && !d.discarded && (
          <Fact label="Falta para enviar" wide>
            {d.missing.length ? d.missing.join(', ') : 'nada: pronto para enviar'}
          </Fact>
        )}
        <Fact label="Pagamento ao prestador">
          {d.paymentMode ? PAYMENT_LABEL[d.paymentMode] : '—'}
        </Fact>
        {d.executedAt && (
          <Fact label="Fotos do prestador">
            <span className="flex flex-wrap gap-1">
              <Badge tone={d.files.hasOrigin ? 'sucesso' : 'neutro'}>Origem</Badge>
              {d.serviceType === 'reboque' && (
                <Badge tone={d.files.hasDestination ? 'sucesso' : 'neutro'}>
                  Destino
                </Badge>
              )}
              <span className="text-xs text-texto-tenue">
                {count(d.files.total, 'arquivo', 'arquivos')}
              </span>
            </span>
          </Fact>
        )}
        {d.scheduledFor && <Fact label="Marcado para">{when(d.scheduledFor)}</Fact>}
        <Fact label="Aberto">
          {when(d.createdAt)}
          {d.createdByName ? ` · ${d.createdByName}` : ''}
        </Fact>
      </Facts>
    </div>
  );
}
